import { Link } from "react-router-dom";
import { ArrowLeft, Building2, Phone, Mail, FileText, User, Code2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const COPY = {
  de: {
    back: "Zurück",
    overline: "— Rechtliches",
    title: "Impressum",
    subtitle: "Angaben gemäss Schweizer Bundesgesetz gegen den unlauteren Wettbewerb (UWG)",
    home: "Zur Startseite",
    rights: "Alle Rechte vorbehalten.",
    company: "Firma",
    contact: "Kontakt",
    tax: "Handelsregister & MwSt.",
    representation: "Vertretungsberechtigte Personen",
    content_responsible: "Verantwortlich für den Inhalt",
    webdesign: "Webdesign & Konzeption",
    webdesign_body:
      "Alle Rechte vorbehalten. Die Gestaltung, Texte, Bilder und Inhalte dieser Website sind urheberrechtlich geschützt. Eine Vervielfältigung, Übernahme oder Nutzung – auch auszugsweise – ist ohne vorherige schriftliche Zustimmung nicht gestattet und kann rechtlich verfolgt werden.",
    uid_label: "CH-UID-Nr.",
    vat_label: "CH-MwSt.-Nr.",
    ceo: "Geschäftsführer",
    phone_label: "Telefon",
    email_label: "E-Mail",
  },
  en: {
    back: "Back",
    overline: "— Legal",
    title: "Imprint",
    subtitle: "Information according to Swiss Federal Act against Unfair Competition (UWG)",
    home: "Back to home",
    rights: "All rights reserved.",
    company: "Company",
    contact: "Contact",
    tax: "Commercial register & VAT",
    representation: "Authorised representatives",
    content_responsible: "Responsible for content",
    webdesign: "Web design & concept",
    webdesign_body:
      "Design and implementation of this website are subject to copyright of the designer. Any reproduction or imitation will be prosecuted legally.",
    uid_label: "Swiss business identification no.",
    vat_label: "Swiss VAT no.",
    ceo: "Managing Director",
    phone_label: "Phone",
    email_label: "Email",
  },
  fr: {
    back: "Retour",
    overline: "— Mentions légales",
    title: "Mentions légales",
    subtitle: "Informations selon la loi fédérale suisse contre la concurrence déloyale (LCD)",
    home: "Retour à l'accueil",
    rights: "Tous droits réservés.",
    company: "Entreprise",
    contact: "Contact",
    tax: "Registre du commerce & TVA",
    representation: "Personnes habilitées à représenter",
    content_responsible: "Responsable du contenu",
    webdesign: "Conception web",
    webdesign_body:
      "Le design et la réalisation de ce site sont soumis au droit d'auteur de la conceptrice. Toute reproduction ou imitation fera l'objet de poursuites judiciaires.",
    uid_label: "N° d'identification d'entreprise CH",
    vat_label: "N° de TVA CH",
    ceo: "Directeur général",
    phone_label: "Téléphone",
    email_label: "E-mail",
  },
  it: {
    back: "Indietro",
    overline: "— Note legali",
    title: "Impressum",
    subtitle: "Informazioni secondo la legge federale svizzera contro la concorrenza sleale (LCSl)",
    home: "Torna alla home",
    rights: "Tutti i diritti riservati.",
    company: "Azienda",
    contact: "Contatto",
    tax: "Registro di commercio & IVA",
    representation: "Persone autorizzate alla rappresentanza",
    content_responsible: "Responsabile del contenuto",
    webdesign: "Web design & concetto",
    webdesign_body:
      "Il design e la realizzazione di questo sito sono soggetti al diritto d'autore della designer. Qualsiasi riproduzione o imitazione verrà perseguita legalmente.",
    uid_label: "N° d'identificazione d'impresa CH",
    vat_label: "N° IVA CH",
    ceo: "Direttore generale",
    phone_label: "Telefono",
    email_label: "E-mail",
  },
};

const CEO_NAME = "Marko Mandic";
const COMPANY = {
  name: "Fritz Kamm AG",
  street: "Zeughausstrasse 12",
  zip_city: "8887 Mels",
  country: "Schweiz",
  phone_display: "+41 81 723 16 42",
  phone_href: "+41817231642",
  email: "sekretariat@f-kamm.ch",
  uid: "CHE-101.174.838",
  vat: "CHE-101.174.838 MWST",
};

// small helper for consistent info-row rendering
function Row({ icon: Icon, label, children }) {
  return (
    <div className="border border-white/10 bg-[#121214] p-6 md:p-7">
      <div className="overline mb-3 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <div className="text-white leading-relaxed">{children}</div>
    </div>
  );
}

export default function Impressum() {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white">
      <header className="border-b border-white/10">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="impressum-back-home" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 border border-white/30 flex items-center justify-center font-display font-semibold text-[13px] tracking-tighter">
              FK
            </div>
            <span className="font-display text-[15px] font-semibold tracking-tight">
              Fritz Kamm
            </span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-zinc-400 hover:text-white">
            <ArrowLeft className="w-3.5 h-3.5" />
            {c.back}
          </Link>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="overline mb-5">{c.overline}</div>
        <h1
          data-testid="impressum-title"
          className="font-display text-4xl md:text-6xl font-semibold tracking-tight leading-[0.95] mb-4"
        >
          {c.title}
        </h1>
        <p className="text-zinc-400 text-sm md:text-base leading-relaxed mb-12 max-w-2xl">
          {c.subtitle}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Firma */}
          <Row icon={Building2} label={c.company}>
            <div className="font-display text-lg md:text-xl mb-1">{COMPANY.name}</div>
            <div className="text-zinc-300 text-[15px]">
              {COMPANY.street}<br />
              {COMPANY.zip_city}<br />
              {COMPANY.country}
            </div>
          </Row>

          {/* Kontakt */}
          <Row icon={Phone} label={c.contact}>
            <div className="space-y-2 text-[15px]">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-zinc-500" />
                <a
                  href={`tel:${COMPANY.phone_href}`}
                  data-testid="impressum-phone"
                  className="hover:text-zinc-300 transition-colors"
                >
                  {COMPANY.phone_display}
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-zinc-500" />
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="impressum-email"
                  className="hover:text-zinc-300 transition-colors break-all"
                >
                  {COMPANY.email}
                </a>
              </div>
            </div>
          </Row>

          {/* UID / MwSt */}
          <Row icon={FileText} label={c.tax}>
            <div className="space-y-2 text-[15px]">
              <div>
                <span className="text-zinc-500 text-[12px] uppercase tracking-[0.14em] block mb-1">{c.uid_label}</span>
                <span className="font-mono tabular-nums">{COMPANY.uid}</span>
              </div>
              <div>
                <span className="text-zinc-500 text-[12px] uppercase tracking-[0.14em] block mb-1">{c.vat_label}</span>
                <span className="font-mono tabular-nums">{COMPANY.vat}</span>
              </div>
            </div>
          </Row>

          {/* Vertretung */}
          <Row icon={User} label={c.representation}>
            <div className="font-display text-lg md:text-xl">{CEO_NAME}</div>
            <div className="text-zinc-400 text-[13px] mt-1">{c.ceo}</div>
          </Row>

          {/* Content responsible — full-width row */}
          <div className="md:col-span-2">
            <Row icon={User} label={c.content_responsible}>
              <div className="font-display text-lg md:text-xl">{CEO_NAME}</div>
              <div className="text-zinc-400 text-[13px] mt-1">{c.ceo}</div>
            </Row>
          </div>

          {/* Web design credit — discrete line at the bottom of the imprint */}
          <div className="md:col-span-2">
            <Row icon={Code2} label={c.webdesign}>
              <div className="text-[15px]">
                <span className="text-zinc-300">Petra Osterode</span>
                <span className="text-zinc-600"> · </span>
                <span className="text-zinc-400">Femmes Marketing</span>
              </div>
              <div className="mt-2 text-[12px] text-zinc-500">
                {c.webdesign_body}
              </div>
            </Row>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/10 mt-12 py-8">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-[12px] text-zinc-500 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <span>© {new Date().getFullYear()} Fritz Kamm · {c.rights}</span>
          <Link to="/" className="hover:text-white transition-colors">{c.home}</Link>
        </div>
      </footer>
    </div>
  );
}
